// biome-ignore-all lint/suspicious/noExplicitAny: generic type 'any' for data structures
import { Snackbar } from '@mui/material';
import { Alert, AlertDescription } from '@tempo-io/sui-alert';
import { translateDynamicKey } from '@tempo-io/translations';
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';


type SnackbarState = {
  message: string;
  severity?: 'error' | 'warning' | 'info' | 'success';
};

type SnackbarContextType = {
  state?: SnackbarState;
  setMessage: Dispatch<SetStateAction<SnackbarState | undefined>>;
  onGlobalError: (error: any, variables: unknown) => void;
};

export const SnackbarContext = createContext<SnackbarContextType>({
  setMessage: () => {
    return;
  },
  onGlobalError: () => {
    return;
  },
});

type SnackbarProviderProps = {
  children: ReactNode;
};

export const useSetSnackbarGlobalError = () => useContext(SnackbarContext).onGlobalError;
export const useSetSnackbarMessage = () => useContext(SnackbarContext).setMessage;

export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const [state, setState] = useState<SnackbarState>();
  const { t } = useTranslation('translation');

  const snackBarOpen = Boolean(state);

  const handleClose = () => {
    setState(undefined);
  };

  // casting to AxiosError has no message property
  const handleGlobalError = (error: any) => {
    const responseData: Record<string, unknown> = error?.response?.data ?? {};
    const message = responseData.message as string || '';

    // We can use {{template}} substitution in the translation string for any of the response data properties.
    let key: string;
    //The most common pattern is intake.serverErrors.<error_message>
    if (translateDynamicKey(t, `intake.serverErrors.${message}`, '')) {
      key = `intake.serverErrors.${message}`;
    } else if (translateDynamicKey(t, message, '')) {
      key = message;
    } else {
      key = 'intake.serverErrors.generic';
    }
    setState({
      message: translateDynamicKey(t, key, message, responseData),
      severity: 'error',
    });
  };

  const context = {
    state,
    setMessage: setState,
    onGlobalError: handleGlobalError,
  };

  return (
    <SnackbarContext.Provider value={context}>
      {children}
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          data-testid="SuccessOutlinedIcon"
          onDismiss={handleClose}
          variant={state?.severity ?? 'info'}
          style={{ position: 'relative', left: 36, width: '100%' }}
        >
          <AlertDescription>{state?.message}</AlertDescription>
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
